import { useLayoutEffect, useState } from 'react';
import type { RefObject } from 'react';

/**
 * Place a popover as a fixed overlay anchored to its trigger — and keep it anchored while the page scrolls.
 *
 * views/layout.ejs's PFOverlay does both (`place()` plus `window.addEventListener('scroll', reposition, true)`),
 * which is why an open menu on the EJS page follows its button when you scroll. A popover that measures once on
 * open is correct until the first scroll, then it hangs in mid-air away from its trigger. The shared `Menu`
 * component already carries this behaviour; this hook is for the popovers that draw their own panel.
 *
 * The geometry is PFOverlay's, to the pixel: MARGIN 8 from every viewport edge, GAP 4 from the trigger, right
 * alignment unless asked for left, flipped above the trigger when there is no room below, and clamped so a
 * trigger near an edge still opens a reachable panel.
 */
export function useAnchored(
  open: boolean,
  triggerRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLElement | null>,
  {
    gap = 4,
    align = 'right',
    fallbackWidth = 300,
    fallbackHeight = 260,
    onClose,
  }: {
    gap?: number;
    align?: 'left' | 'right';
    fallbackWidth?: number;
    fallbackHeight?: number;
    /** PFOverlay's dismissal contract: a pointer down outside, or Escape, closes the open panel. */
    onClose?: () => void;
  } = {},
) {
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setAt(null);
      return;
    }
    const place = () => {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel) return;
      const MARGIN = 8;
      const r = trigger.getBoundingClientRect();
      const w = panel.offsetWidth || fallbackWidth;
      const h = panel.offsetHeight || fallbackHeight;
      const desired = align === 'left' ? r.left : r.right - w;
      const left = Math.min(Math.max(MARGIN, desired), Math.max(MARGIN, window.innerWidth - w - MARGIN));
      const below = window.innerHeight - r.bottom - MARGIN;
      let top = h + gap <= below ? r.bottom + gap : r.top - h - gap;
      const maxTop = Math.max(MARGIN, window.innerHeight - h - MARGIN);
      top = Math.min(Math.max(MARGIN, top), maxTop);
      // Value equality, not a fresh object: the effect re-subscribes when its caller passes an inline onClose,
      // and a new {top,left} every run re-renders into the effect again — "Maximum update depth exceeded".
      setAt((prev) => (prev && prev.top === top && prev.left === left ? prev : { top, left }));
    };
    place();
    window.addEventListener('resize', place);
    // Capture phase, as PFOverlay listens: a popover inside a scrolling pane must follow its trigger too.
    window.addEventListener('scroll', place, true);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (panelRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      onClose?.();
    };
    if (onClose) {
      document.addEventListener('keydown', onKey);
      document.addEventListener('pointerdown', onDown, true);
    }
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown, true);
    };
  }, [open, triggerRef, panelRef, gap, align, fallbackWidth, fallbackHeight, onClose]);

  return at;
}
